/**
 * components/charts/Histogram — a vertical frequency histogram, built to the dataviz
 * skill. Two colour jobs share one geometry:
 *
 *  - `diverging` (R-multiple distribution): each bar's hue follows the SIGN of its bin —
 *    losing bins loss-red left of 0R, winning bins profit-green right of it — with a
 *    recessive 0R rule between them. Polarity around a fixed centre → the terminal's
 *    diverging poles.
 *  - `sequential` (committee confidence): one magnitude, so a single accent hue.
 *
 * Bars are thin with a 4px rounded top squared to the baseline, a 2px surface gap between
 * neighbours, recessive gridlines and a per-bar native tooltip. Count labels are drawn
 * selectively (only on non-empty bars) so the axis stays quiet.
 */
import { useMemo } from 'react';

import type { HistoBin } from '../../lib/analytics';

interface HistogramProps {
  readonly bins: readonly HistoBin[];
  readonly mode: 'diverging' | 'sequential';
  /** Axis unit suffix shown under the endpoints (e.g. 'R' or '%'). */
  readonly unit?: string;
  readonly height?: number;
  readonly ariaLabel?: string;
}

const VIEW_W = 720;
const PAD = { l: 34, r: 12, t: 14, b: 30 } as const;

export function Histogram({ bins, mode, unit = '', height = 220, ariaLabel }: HistogramProps): JSX.Element {
  const geo = useMemo(() => {
    if (bins.length === 0) return null;
    const plotW = VIEW_W - PAD.l - PAD.r;
    const plotH = height - PAD.t - PAD.b;
    const maxCount = Math.max(1, ...bins.map((b) => b.count));
    const slot = plotW / bins.length;
    const gap = Math.min(6, slot * 0.16);
    const barW = slot - gap;
    const y = (c: number): number => PAD.t + (1 - c / maxCount) * plotH;
    const baseY = PAD.t + plotH;
    const ticks = [maxCount, Math.round(maxCount / 2), 0].filter((v, i, a) => a.indexOf(v) === i);
    return { plotW, plotH, maxCount, slot, gap, barW, y, baseY, ticks };
  }, [bins, height]);

  if (!geo) {
    return <div className="state"><span className="muted">NO DATA YET</span></div>;
  }

  const first = bins[0]!;
  const last = bins[bins.length - 1]!;

  return (
    <div className="chart chart--histo">
      <svg viewBox={`0 0 ${VIEW_W} ${height}`} role="img" aria-label={ariaLabel ?? 'Distribution histogram'} preserveAspectRatio="none">
        {/* recessive y grid + count labels */}
        {geo.ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.y(t)} y2={geo.y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={PAD.l - 6} y={geo.y(t) + 3} fontSize={11} fill="var(--text-muted)" textAnchor="end" fontFamily="var(--font-mono)">
              {t}
            </text>
          </g>
        ))}

        {bins.map((b, i) => {
          const center = (b.x0 + b.x1) / 2;
          const color =
            mode === 'sequential'
              ? 'var(--accent)'
              : center > 0 ? 'var(--profit)' : center < 0 ? 'var(--loss)' : 'var(--text-muted)';
          const x = PAD.l + i * geo.slot + geo.gap / 2;
          const barH = geo.baseY - geo.y(b.count);
          return (
            <g key={i}>
              <rect
                x={x} y={geo.y(b.count)} width={geo.barW} height={Math.max(0, barH)}
                rx={3} fill={color} fillOpacity={b.count === 0 ? 0.12 : 0.9}
              >
                <title>{`${b.label}${unit}: ${b.count} trade${b.count === 1 ? '' : 's'}`}</title>
              </rect>
              {b.count > 0 && (
                <text x={x + geo.barW / 2} y={geo.y(b.count) - 4} fontSize={11} fill="var(--text-secondary)" textAnchor="middle" fontFamily="var(--font-mono)">
                  {b.count}
                </text>
              )}
            </g>
          );
        })}

        {/* zero rule for the diverging case */}
        {mode === 'diverging' && first.x0 < 0 && last.x1 > 0 && (() => {
          const zeroIdx = bins.findIndex((b) => b.x0 <= 0 && b.x1 > 0);
          if (zeroIdx < 0) return null;
          const zx = PAD.l + zeroIdx * geo.slot + (0 - bins[zeroIdx]!.x0) / (bins[zeroIdx]!.x1 - bins[zeroIdx]!.x0) * geo.slot;
          return <line x1={zx} x2={zx} y1={PAD.t} y2={geo.baseY} stroke="var(--viz-axis)" strokeWidth={1} strokeDasharray="3 3" />;
        })()}

        {/* baseline + x endpoints */}
        <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.baseY} y2={geo.baseY} stroke="var(--viz-axis)" strokeWidth={1} />
        <text x={PAD.l} y={height - 8} fontSize={11} fill="var(--text-muted)">{fmtEdge(first.x0)}{unit}</text>
        <text x={VIEW_W - PAD.r} y={height - 8} fontSize={11} fill="var(--text-muted)" textAnchor="end">{fmtEdge(last.x1)}{unit}</text>
      </svg>
    </div>
  );
}

function fmtEdge(v: number): string {
  const s = Math.abs(v) % 1 === 0 ? Math.abs(v).toFixed(0) : Math.abs(v).toFixed(1);
  return `${v < 0 ? '−' : ''}${s}`;
}
